/**
 * Onde a pessoa parou de ler, para o acervo oferecer a volta.
 *
 * **É a coisa que um app de estudo mais faz e que esta tela não fazia.** Quem
 * lê uma lei de 2.083 artigos não termina numa sentada: sai, volta no dia
 * seguinte, e reencontrava o catálogo inteiro - tinha de lembrar qual lei era e
 * em que página tinha ficado.
 *
 * **No navegador, e não no servidor**, por enquanto: é preferência de leitura,
 * como o recolhimento da barra e a forma de ver o acervo. Guardar no servidor
 * exigiria tabela, rota e sincronização entre aparelhos - e o ganho de atravessar
 * aparelhos só aparece para quem estuda no celular e no computador, que é
 * pergunta de produto, não de tela. Quando ela for respondida, o formato aqui já
 * é o que a rota mandaria.
 */
const CHAVE = 'titularis:legislacao-ultima-leitura';

export interface LeituraRecente {
  leiId: string;
  /** O nome da lei, guardado junto: o cartão da volta não espera consulta. */
  titulo: string;
  pagina: number;
  /** ISO. Serve para envelhecer o convite - ninguém volta de dois meses atrás. */
  quando: string;
}

/** Depois disto, a volta não é mais uma volta: é um catálogo. */
const VALIDADE_EM_DIAS = 30;

export function guardarLeitura(leitura: Omit<LeituraRecente, 'quando'>): void {
  try {
    globalThis.localStorage?.setItem(
      CHAVE,
      JSON.stringify({ ...leitura, quando: new Date().toISOString() }),
    );
  } catch {
    /*
     * Navegador com armazenamento bloqueado (anônima estrita, política de
     * empresa) não pode derrubar a leitura: isto é conveniência, e o preço de
     * perdê-la é a pessoa achar a lei no catálogo, como antes.
     */
  }
}

export function lerLeituraRecente(agora: Date = new Date()): LeituraRecente | null {
  try {
    const bruto = globalThis.localStorage?.getItem(CHAVE);
    if (!bruto) return null;

    const dados = JSON.parse(bruto) as Partial<LeituraRecente>;
    if (!dados.leiId || !dados.titulo || !dados.quando) return null;
    if (typeof dados.pagina !== 'number' || dados.pagina < 1) return null;

    const dias = (agora.getTime() - new Date(dados.quando).getTime()) / 86_400_000;
    if (!Number.isFinite(dias) || dias > VALIDADE_EM_DIAS) return null;

    return dados as LeituraRecente;
  } catch {
    return null;
  }
}

export function esquecerLeitura(): void {
  try {
    globalThis.localStorage?.removeItem(CHAVE);
  } catch {
    /* Ver `guardarLeitura`. */
  }
}
